/**
 * Menu del pannello di amministrazione (Task Asana 1).
 *
 * Gruppi di servizio (Condivisi, Social Marketing, CasaDiRiposoPerTe,
 * R&CO. / Affidya, Portali esterni) seguiti dalle sezioni di gestione
 * piattaforma già esistenti, tutte raggiungibili. Gli id dei moduli NON
 * cambiano: sono salvati in admin_crm_users.modules, nei profili backend,
 * nei badge e nei link /admin?module=… di email e push.
 */
import {
  Activity,
  Bell,
  Brain,
  Building,
  Building2,
  CalendarDays,
  ClipboardList,
  FileText,
  Globe,
  HardDrive,
  Headphones,
  Home,
  BookOpen,
  Hotel,
  Landmark,
  LayoutDashboard,
  Mail,
  Map,
  Megaphone,
  MessageCircle,
  Newspaper,
  Share2,
  Bot,
  KeyRound,
  Link2,
  PiggyBank,
  Pill,
  Receipt,
  Rows3,
  Settings,
  Shield,
  ShieldAlert,
  ShoppingCart,
  Star,
  UserPlus,
  UserSearch,
  Users,
  Wrench,
} from "lucide-react";

export const DASHBOARD_MODULE_ID = "commerciale-dashboard";

export const DASHBOARD_TABS = [
  { id: "panoramica", label: "Panoramica" },
  { id: "operativo", label: "Operativo" },
  { id: "economico", label: "Economico" },
  { id: "marketing-social", label: "Marketing e Social" },
];

export const ADMIN_NAV_GROUPS = [
  {
    id: "condivisi",
    title: "Condivisi",
    primary: true,
    items: [
      {
        id: DASHBOARD_MODULE_ID,
        label: "Dashboard e report",
        icon: LayoutDashboard,
        description: "Panoramica, analisi operative per servizio, analisi economiche, report ed esportazioni.",
        // Voce unica: chi aveva "Dashboard" o "Report" continua a vederla.
        alsoAllowedBy: ["commerciale-report"],
        keywords: ["report", "dashboard", "statistiche", "economico", "fatturato", "csv", "esporta"],
      },
      {
        id: "strutture",
        label: "Strutture",
        icon: Building,
        description: "Anagrafica comune. Si possono inserire strutture senza account e mostrarle o nasconderle sull'app CasaDiRiposoPerTe.",
      },
      {
        id: "email-marketing",
        label: "Email marketing",
        icon: Mail,
        description: "Campagne, liste e contenuti email. Ogni campagna mantiene il proprio contesto di servizio.",
      },
      {
        id: "marketing-social",
        label: "Marketing e Social",
        icon: Share2,
        description: "Pianificazione contenuti, canali social, asset, risultati e integrazioni.",
      },
    ],
  },
  {
    id: "social-marketing",
    title: "Social Marketing",
    primary: true,
    items: [
      {
        id: "social-marketing-procedura",
        label: "Procedura bot",
        icon: BookOpen,
        superAdminOnly: true,
        description: "Passi per collegare i bot Grok e far arrivare in Affidya i post generati.",
        keywords: ["grok", "bot", "procedura", "token", "istruzioni"],
      },
      {
        id: "social-marketing-affidya",
        label: "Affidya",
        icon: Bot,
        superAdminOnly: true,
        description: "Post social di Affidya generati dai bot Grok, da modificare e approvare con orario di pubblicazione.",
        keywords: ["social", "postiz", "grok", "affidya", "approvazione"],
      },
      {
        id: "social-marketing-casadiriposoperte",
        label: "Casadiriposoperte",
        icon: Home,
        superAdminOnly: true,
        description: "Post social di Casadiriposoperte generati dai bot Grok, da modificare e approvare con orario di pubblicazione.",
        keywords: ["social", "postiz", "grok", "casadiriposoperte", "approvazione"],
      },
      {
        id: "social-marketing-rco",
        label: "R&CO Group",
        icon: Globe,
        superAdminOnly: true,
        description: "Post social di R&CO Group generati dai bot Grok, da modificare e approvare con orario di pubblicazione.",
        keywords: ["social", "postiz", "grok", "rco", "approvazione"],
      },
      {
        id: "social-marketing-postiz",
        label: "Postiz",
        icon: KeyRound,
        superAdminOnly: true,
        description: "Chiave API Postiz, canali dei tre marchi, token dei bot Grok e avviso di approvazione.",
        keywords: ["postiz", "token", "webhook", "api", "social"],
      },
      {
        id: "social-marketing-meta",
        label: "Collegamento Meta",
        icon: Link2,
        superAdminOnly: true,
        description: "App Meta unica per Affidya, Casadiriposoperte e R&CO Group: pubblica i post approvati su Facebook e Instagram.",
        keywords: ["meta", "facebook", "instagram", "app id", "token", "pagina"],
      },
    ],
  },
  {
    id: "cdrpt",
    title: "CasaDiRiposoPerTe",
    primary: true,
    items: [
      {
        id: "leads-admin",
        label: "Richieste famiglie",
        icon: Users,
        description: "Richieste delle famiglie, richieste dal sito e pratiche Ospiti in Rete.",
        countTitle: "richieste in stato Nuovo",
        keywords: ["famiglie", "lead", "ospiti in rete", "cdrpt"],
      },
      {
        id: "cdrpt-partnership",
        label: "Partnership strutture",
        icon: Building,
        description: "Percorso commerciale, accordi, condizioni commissionali e attivazione dei partner CasaDiRiposoPerTe.",
        keywords: ["partnership", "partner", "accordi", "commissioni", "strutture"],
      },
      {
        id: "cdrpt-registrazioni",
        label: "Registrazioni portale",
        icon: UserPlus,
        description: "Account creati su casadiriposoperte.it. L'attivazione avvia la prova di 14 giorni e pubblica la scheda nel catalogo. Da qui si entra nell'account sul sito.",
        keywords: ["registrazioni", "prova", "catalogo", "portale", "abbonamento"],
      },
      {
        id: "cdrpt-vetrina",
        label: "Pacchetti vetrina",
        icon: Megaphone,
        superAdminOnly: true,
        description: "Giorni e costo della visibilità sponsorizzata sul catalogo di casadiriposoperte.it.",
        keywords: ["vetrina", "sponsor", "visibilità", "pacchetti", "catalogo"],
      },
      {
        id: "commerciale-candidature-cdrpt",
        label: "Candidature CDRPT",
        icon: UserSearch,
        description: "Candidature di lavoro ricevute dal sito CasaDiRiposoPerTe.",
        countTitle: "candidature in stato Nuovo",
        keywords: ["candidature", "casadiriposoperte"],
      },
      {
        id: "commerciale-whatsapp",
        label: "WhatsApp",
        icon: MessageCircle,
        description: "Risposte delle strutture, conversazioni e configurazione Meta.",
        countTitle: "disponibilità posti da confermare",
      },
      {
        id: "commerciale-kit-cdrpt",
        label: "Kit commerciale CDRPT",
        icon: FileText,
        description: "Playbook e documenti commerciali di CasaDiRiposoPerTe.",
        alsoAllowedBy: ["commerciale-playbook"],
        keywords: ["playbook", "script", "contratto", "kit"],
      },
    ],
  },
  {
    id: "rco-affidya",
    title: "R&CO. / Affidya",
    primary: true,
    items: [
      {
        id: "commerciale-visite",
        label: "Le mie visite",
        icon: CalendarDays,
        description: "Visite alle strutture, bozze, opportunità e follow-up.",
        keywords: ["visite", "tablet", "appuntamenti", "follow-up"],
      },
      {
        id: "commerciale-pipeline",
        label: "Pipeline Affidya",
        icon: Rows3,
        description: "Pipeline commerciale dei lead strutture per la suite Affidya (distinta dal percorso famiglie CDRPT).",
        keywords: ["pipeline", "commerciale"],
      },
      {
        id: "commerciale-attivita",
        label: "Attività",
        icon: Activity,
        description: "Promemoria e attività commerciali sui lead strutture.",
      },
      {
        id: "commerciale-demo",
        label: "Demo",
        icon: CalendarDays,
        description: "Programmazione, preparazione e chiusura delle demo.",
      },
      {
        id: "demo-account",
        label: "Account Demo",
        icon: ShieldAlert,
        superAdminOnly: true,
        description: "Ripristino sicuro dell'ambiente demo della fiera.",
      },
      {
        id: "commerciale-onboarding",
        label: "Inviti onboarding",
        icon: UserPlus,
        description: "Account struttura, link di registrazione e stato fino all'attivazione.",
      },
      {
        id: "commerciale-richieste-sito",
        label: "Richieste sito R&CO.",
        icon: Globe,
        description: "Contatti e richieste demo ricevuti da rco-group.it.",
        countTitle: "richieste in stato Nuovo",
      },
      {
        id: "commerciale-lead-strutture",
        label: "Lead strutture",
        icon: Building2,
        description: "Prospect commerciali, consenso di contatto e inviti alla registrazione Affidya.",
        countTitle: "lead in stato Nuovo (totale da lavorare, non attività urgenti)",
      },
      {
        id: "commerciale-kit-affidya",
        label: "Kit commerciale AFFIDYA",
        icon: FileText,
        description: "Playbook e documenti commerciali della suite Affidya.",
        alsoAllowedBy: ["commerciale-playbook"],
        keywords: ["playbook", "script", "contratto", "kit"],
      },
    ],
  },
  {
    id: "portali-esterni",
    title: "Portali esterni",
    primary: true,
    items: [
      {
        id: "immobili",
        label: "Immobiliare",
        icon: Building2,
        description: "Catalogo immobili, richieste, accessi e portale.",
      },
    ],
  },
  // ---- Gestione piattaforma: sezioni esistenti, invariate e sempre raggiungibili ----
  {
    id: "piattaforma",
    title: "Piattaforma Affidya",
    items: [
      { id: "dashboard", label: "Panoramica piattaforma", icon: LayoutDashboard, keywords: ["dashboard", "piattaforma"] },
      { id: "utenti", label: "Utenti", icon: Users },
      { id: "servizi", label: "Servizi", icon: Settings },
    ],
  },
  {
    id: "canali-collegati",
    title: "Altri portali e canali",
    items: [
      { id: "farmacie", label: "Portale Farmacia", icon: Pill, keywords: ["farmacia"] },
      { id: "casadiriposoperte", label: "Portale CasaDiRiposoPerTe", icon: Home, keywords: ["casadiriposoperte", "monitor", "ai"] },
      { id: "commerciale-trustpilot", label: "Trustpilot", icon: Star, superAdminOnly: true, countTitle: "in programma" },
    ],
  },
  {
    id: "servizi-business",
    title: "Servizi e business",
    items: [
      { id: "risparmio-listino", label: "Listino Risparmio", icon: PiggyBank },
      { id: "richieste-posto", label: "Richieste posto ospite", icon: ClipboardList },
      { id: "trova-ospiti", label: "Trova Ospiti", icon: Users, badge: "BETA" },
      { id: "trova-personale", label: "Trova Personale", icon: UserSearch, badge: "BETA" },
      { id: "alloggiati-web", label: "Alloggiati Web", icon: Hotel },
      { id: "adesioni-admin", label: "Adesioni gruppi", icon: ShoppingCart },
    ],
  },
  {
    id: "supporto",
    title: "Supporto",
    items: [{ id: "assistenza", label: "Assistenza", icon: Headphones }],
  },
  {
    id: "contenuti",
    title: "Contenuti",
    items: [
      { id: "newsletters", label: "Newsletter", icon: Newspaper },
      { id: "modulistica-admin", label: "Modulistica", icon: FileText },
    ],
  },
  {
    id: "recruiting",
    title: "Recruiting",
    items: [
      { id: "recruiting-candidati", label: "Candidati recruiting", icon: UserSearch },
      { id: "richieste-personale-admin", label: "Richieste personale", icon: UserSearch },
    ],
  },
  {
    id: "configurazione",
    title: "Configurazione",
    items: [
      { id: "config", label: "Configurazione", icon: FileText },
      { id: "bank-details", label: "Coordinate bancarie", icon: Landmark },
      { id: "affidya-fic", label: "Fatturazione abbonamenti", icon: Receipt },
      { id: "ai-config", label: "Intelligenza Artificiale", icon: Brain },
      { id: "whatsapp-operativo", label: "WhatsApp operativo", icon: MessageCircle, superAdminOnly: true },
      { id: "google-maps", label: "Google Maps API", icon: Map },
      { id: "push-notifiche", label: "Push notifiche", icon: Bell },
    ],
  },
  {
    id: "sistema",
    title: "Sistema",
    items: [
      { id: "admin-utenti-limitati", label: "Utenti admin", icon: Shield },
      { id: "manutenzione", label: "Manutenzione", icon: Wrench },
      { id: "sistema", label: "Server e backup", icon: HardDrive },
      { id: "sicurezza", label: "Sicurezza", icon: ShieldAlert },
      { id: "audit", label: "Audit Log", icon: Shield },
    ],
  },
];

export const ADMIN_MODULES = ADMIN_NAV_GROUPS.flatMap((group) =>
  group.items.map((item) => ({ ...item, groupId: group.id, groupTitle: group.title }))
);

/**
 * Alias dei vecchi id (link salvati, email, push). Ogni alias porta al modulo
 * canonico e, per la dashboard, alla vista corrispondente.
 */
export const KIT_TABS = [
  { id: "playbook", label: "Playbook" },
  { id: "documenti", label: "Documenti commerciali" },
];

export const MODULE_ALIASES = {
  "commerciale-report": { module: DASHBOARD_MODULE_ID, tab: "operativo" },
  "dashboard-report": { module: DASHBOARD_MODULE_ID },
  "commerciale-playbook": { module: "commerciale-kit-affidya", tab: "playbook" },
  "commerciale-richieste-cdrpt": { module: "leads-admin" },
  "ospiti-rete-crm": { module: "leads-admin" },
  "ospiti-rete-condivisi": { module: "leads-admin" },
};

export function resolveAdminTarget(moduleId, tab) {
  const raw = String(moduleId || "").trim();
  const alias = MODULE_ALIASES[raw];
  const module = alias ? alias.module : raw;
  let resolvedTab = null;
  if (module === DASHBOARD_MODULE_ID) {
    const candidate = tab || (alias && alias.tab) || null;
    if (DASHBOARD_TABS.some((t) => t.id === candidate)) resolvedTab = candidate;
  } else if (String(module).startsWith("commerciale-kit-")) {
    const candidate = tab || (alias && alias.tab) || "playbook";
    resolvedTab = KIT_TABS.some((t) => t.id === candidate) ? candidate : "playbook";
  }
  return { module, tab: resolvedTab };
}

export function resolveAdminModule(moduleId) {
  return resolveAdminTarget(moduleId).module;
}

export function findAdminModule(moduleId) {
  return ADMIN_MODULES.find((m) => m.id === moduleId) || null;
}

/** Il modulo è nell'elenco moduli dell'operatore (con le equivalenze storiche). */
export function hasAdminModule(modules, moduleId) {
  if (!Array.isArray(modules)) return true;
  if (modules.includes(moduleId)) return true;
  if (moduleId === "leads-admin" && modules.includes("commerciale-richieste-cdrpt")) return true;
  const meta = findAdminModule(moduleId);
  return Boolean(meta && (meta.alsoAllowedBy || []).some((id) => modules.includes(id)));
}

/** Stessa regola di visibilità di prima: nessun ampliamento per effetto del nuovo menu. */
export function isNavItemVisible(item, { scope, modules }) {
  if (item.superAdminOnly) return scope === "super_admin";
  if (scope !== "crm_operatore") return true;
  return hasAdminModule(modules, item.id);
}

export function visibleNavGroups(user, counts = {}) {
  return ADMIN_NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items
      .filter((item) => isNavItemVisible(item, user))
      .map((item) => {
        const count = Number(counts[item.id]) || 0;
        return count > 0 ? { ...item, count } : item;
      }),
  })).filter((group) => group.items.length > 0);
}

/** Permessi delle viste della Dashboard e report. */
export function dashboardTabAccess({ scope, modules, permissions }) {
  const isSuper = scope === "super_admin";
  // Operatore con documento legacy senza elenco moduli: il backend gli concede
  // tutti i moduli (user_has_module), quindi lo stesso vale qui.
  const has = (id) => isSuper || (scope === "crm_operatore" && (!Array.isArray(modules) || modules.includes(id)));
  return {
    panoramica: has("commerciale-dashboard"),
    report: has("commerciale-report"),
    operativo: has("commerciale-dashboard") || has("commerciale-report"),
    // Dati economici aperti a tutti gli utenti admin (super admin e operatori CRM),
    // decisione esplicita di Giampaolo (ott. 2026).
    economico: isSuper || scope === "crm_operatore",
    "marketing-social": has("marketing-social"),
    // /stats/dashboard e /stats/report* accettano ora Dashboard OR Report OR Lead
    // strutture: leadStrutture resta esposto per eventuali UI, ma non bloccano più le viste.
    leadStrutture: has("commerciale-lead-strutture"),
  };
}

export function defaultDashboardTab(access) {
  if (access.panoramica) return "panoramica";
  if (access.operativo) return "operativo";
  if (access.economico) return "economico";
  return "panoramica";
}

export function buildAdminBreadcrumb(moduleId, tab) {
  const meta = findAdminModule(moduleId);
  if (!meta) return [];
  const crumbs = [{ label: meta.groupTitle }, { label: meta.label, moduleId: meta.id }];
  if (moduleId === DASHBOARD_MODULE_ID && tab) {
    const t = DASHBOARD_TABS.find((x) => x.id === tab);
    if (t) crumbs.push({ label: t.label });
  } else if (String(moduleId).startsWith("commerciale-kit-") && tab) {
    const t = KIT_TABS.find((x) => x.id === tab);
    if (t) crumbs.push({ label: t.label });
  }
  return crumbs;
}

/** URL canonico: ?module=<id>[&tab=<vista>] mantenendo gli altri parametri. */
export function buildAdminSearch(currentSearch, moduleId, tab) {
  const params = new URLSearchParams(currentSearch || "");
  params.set("module", moduleId);
  const keepsTab = (moduleId === DASHBOARD_MODULE_ID && tab)
    || (String(moduleId).startsWith("commerciale-kit-") && KIT_TABS.some((item) => item.id === tab));
  if (keepsTab) params.set("tab", tab);
  else params.delete("tab");
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}
